import { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import { format, formatDistanceToNow } from 'date-fns';
import {
  SparklesIcon,
  PlayIcon,
  ClockIcon,
  DocumentTextIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const AI_MODELS = {
  anthropic: [
    { id: 'claude-sonnet-4-20250514', name: 'Claude Sonnet 4' },
    { id: 'claude-3-haiku-20240307', name: 'Claude 3 Haiku' }
  ],
  openai: [
    { id: 'gpt-4o', name: 'GPT-4o' },
    { id: 'gpt-4o-mini', name: 'GPT-4o Mini' }
  ],
  google: [
    { id: 'gemini-1.5-pro', name: 'Gemini 1.5 Pro' },
    { id: 'gemini-1.5-flash', name: 'Gemini 1.5 Flash' }
  ]
};

function ReportCard({ report, isSelected, onClick }) {
  const statusColors = {
    completed: 'text-emerald-400',
    pending: 'text-amber-400',
    failed: 'text-red-400'
  };
  
  const StatusIcon = {
    completed: CheckCircleIcon,
    pending: ClockIcon,
    failed: XCircleIcon
  }[report.status];
  
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border transition-all ${
        isSelected 
          ? 'border-primary-500 bg-primary-500/10' 
          : 'border-surface-700 hover:border-surface-600 hover:bg-surface-800/50'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <DocumentTextIcon className="w-5 h-5 text-surface-400" />
          <span className="font-medium text-surface-100 text-sm">
            {report.title || 'Analysis Report'}
          </span>
        </div>
        <StatusIcon className={`w-4 h-4 ${statusColors[report.status]}`} />
      </div>
      <p className="text-xs text-surface-400 mb-2">
        {formatDistanceToNow(new Date(report.createdAt))} ago
      </p>
      <div className="flex items-center gap-2">
        <span className="text-xs bg-surface-700 px-2 py-0.5 rounded text-surface-300">
          {report.aiConfig?.provider}
        </span>
        <span className="text-xs text-surface-500">
          {report.tokenUsage?.total?.toLocaleString() || 0} tokens
        </span>
      </div>
    </button>
  );
}

export default function AIProjectManager() {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [provider, setProvider] = useState('anthropic');
  const [model, setModel] = useState('claude-sonnet-4-20250514');
  
  useEffect(() => {
    loadReports();
  }, []);
  
  useEffect(() => {
    // Update model when provider changes
    setModel(AI_MODELS[provider][0].id);
  }, [provider]);
  
  const loadReports = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getReports({ limit: 20 });
      setReports(response.data.reports);
      if (response.data.reports.length > 0 && !selectedReport) {
        setSelectedReport(response.data.reports[0]);
      }
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };
  
  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await adminApi.runAnalysis({ provider, model });
      toast.success('Analysis complete!');
      setSelectedReport(response.data);
      loadReports();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };
  
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">AI Project Manager</h1>
          <p className="text-surface-400 mt-1">Get AI-powered insights and recommendations</p>
        </div>
      </div>
      
      {/* Analysis Controls */}
      <div className="card">
        <h3 className="text-lg font-semibold text-surface-100 mb-4 flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-primary-400" />
          Run New Analysis
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              AI Provider
            </label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="input"
              disabled={analyzing}
            >
              <option value="anthropic">Anthropic (Claude)</option>
              <option value="openai">OpenAI (GPT)</option>
              <option value="google">Google (Gemini)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">
              Model
            </label>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="input"
              disabled={analyzing}
            >
              {AI_MODELS[provider].map(m => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          
          <div className="md:col-span-2 flex items-end">
            <button
              onClick={runAnalysis}
              disabled={analyzing}
              className="btn-primary w-full md:w-auto"
            >
              {analyzing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <PlayIcon className="w-5 h-5" />
                  Run Analysis
                </>
              )}
            </button>
          </div>
        </div>
        
        <p className="text-xs text-surface-500 mt-4">
          The AI will analyze open issues, usage trends, and platform metrics to provide recommendations.
        </p>
      </div>
      
      {/* Reports section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Reports list */}
        <div className="lg:col-span-1">
          <h3 className="text-sm font-medium text-surface-300 mb-3">Recent Reports</h3>
          
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reports.length === 0 ? (
            <div className="card text-center py-8">
              <DocumentTextIcon className="w-10 h-10 text-surface-500 mx-auto mb-3" />
              <p className="text-surface-400 text-sm">No reports yet</p>
              <p className="text-surface-500 text-xs mt-1">Run your first analysis above</p>
            </div>
          ) : (
            <div className="space-y-2">
              {reports.map(report => (
                <ReportCard
                  key={report._id}
                  report={report}
                  isSelected={selectedReport?._id === report._id}
                  onClick={() => setSelectedReport(report)}
                />
              ))}
            </div>
          )}
        </div>
        
        {/* Report content */}
        <div className="lg:col-span-3">
          {selectedReport ? (
            <div className="card">
              <div className="flex items-start justify-between mb-6 pb-6 border-b border-surface-700">
                <div>
                  <h2 className="text-xl font-semibold text-surface-100">
                    {selectedReport.title || 'Analysis Report'}
                  </h2>
                  <p className="text-surface-400 text-sm mt-1">
                    Generated {format(new Date(selectedReport.createdAt), 'PPP')} at {format(new Date(selectedReport.createdAt), 'p')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs bg-surface-700 px-3 py-1 rounded-full text-surface-300">
                    {selectedReport.aiConfig?.provider} / {selectedReport.aiConfig?.model}
                  </span>
                  {selectedReport.tokenUsage && (
                    <span className="text-xs text-surface-500">
                      {selectedReport.tokenUsage.total?.toLocaleString()} tokens
                    </span>
                  )}
                </div>
              </div>
              
              {selectedReport.status === 'pending' && (
                <div className="flex items-center gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                  <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  <p className="text-amber-400">Analysis in progress...</p>
                </div>
              )}
              
              {selectedReport.status === 'failed' && (
                <div className="flex items-center gap-3 bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-red-400 font-medium">Analysis failed</p>
                    <p className="text-red-400/70 text-sm">{selectedReport.error}</p>
                  </div>
                </div>
              )}
              
              {selectedReport.status === 'completed' && selectedReport.report?.fullReport && (
                <div className="markdown-content prose prose-invert max-w-none">
                  <ReactMarkdown>
                    {selectedReport.report.fullReport}
                  </ReactMarkdown>
                </div>
              )}
              
              {/* Analysis context */}
              {selectedReport.analysisContext && (
                <div className="mt-8 pt-6 border-t border-surface-700">
                  <h4 className="text-sm font-medium text-surface-400 mb-3">Analysis Context</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="bg-surface-700/50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-surface-100">
                        {selectedReport.analysisContext.issueCount}
                      </p>
                      <p className="text-xs text-surface-400">Issues Analyzed</p>
                    </div>
                    <div className="bg-surface-700/50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-surface-100">
                        {selectedReport.analysisContext.companyCount}
                      </p>
                      <p className="text-xs text-surface-400">Companies</p>
                    </div>
                    <div className="bg-surface-700/50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-surface-100">
                        {selectedReport.analysisContext.userCount}
                      </p>
                      <p className="text-xs text-surface-400">Users</p>
                    </div>
                    <div className="bg-surface-700/50 rounded-lg p-3 text-center">
                      <p className="text-lg font-semibold text-surface-100">
                        30
                      </p>
                      <p className="text-xs text-surface-400">Day Window</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card flex flex-col items-center justify-center h-96">
              <SparklesIcon className="w-16 h-16 text-surface-600 mb-4" />
              <h3 className="text-lg font-medium text-surface-300 mb-2">No Report Selected</h3>
              <p className="text-surface-400 text-center max-w-md">
                Run a new analysis or select a previous report from the list to view AI-powered insights and recommendations.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

